// @flow
import * as React from 'react';

import { memoize } from '../modules/memoize';
import { translate, translateWithFallback } from '../modules/l10n';
// types
import type { Translations, TranslateType } from '../modules/l10n';

export type { TranslateType } from 'modules/l10n';

/* 💁 There are basically two ways to use this L10n solution. I've had
situations where one worked better than the other.

1. The higher-order-component `withL10n`.
  - Wrapping a component with this method provides a component with the props
      - translate
      - translations
      - locale

2. The component <Translate /> (found in ./Translate.js).
  - Use this one if all you need is a translation. It has an error boundary so
  we can get more targeted errors when translations are missing.
*/

export type Context = {
  locale: string,
  translations: Translations,
};

/* ⚠️ Warning
 * From the docs:
  > The defaultValue argument is only used by a Consumer when it does not have
  a matching Provider above it in the tree. This can be helpful for testing
  components in isolation without wrapping them. Note: passing undefined as a
  Provider value does not cause Consumers to use defaultValue.

  This means the default locale 'en-us' would not exist if the Provider is
  instantiated with a translations prop and no locale, **locale would be
  undefined**.
*/
export const L10nContext: React$Context<Context> = React.createContext<Context>(
  {
    locale: 'en-us',
    translations: { ...null },
  },
);

export const L10nProvider = L10nContext.Provider;
export const L10nConsumer = L10nContext.Consumer;

export type TranslateWithFallback = (
  key: string,
  fallback: string,
  replacements?: { [key: string]: string | number },
) => string;

export type AddedProps = $ReadOnly<{|
  locale: string,
  translations: Translations,
  translate: TranslateType,
|}>;

export const factory: (
  locale: string,
  translations: Translations,
) => TranslateType = memoize(
  (locale: string, translations: Translations): TranslateType =>
    (key, replacements = { ...null }) =>
      translate(key, replacements, translations, locale),
  (...args) => JSON.stringify(args),
);

export const withFallbackFactory: (
  translations: Translations,
) => TranslateWithFallback = memoize(
  (translations: Translations) =>
    (key, fallback, replacements = { ...null }) =>
      translateWithFallback(key, replacements, translations, fallback),
  (...args) => JSON.stringify(args),
);

// tried to mimic the typing happening in the docs
// https://flow.org/en/docs/react/hoc/
// eslint-disable-next-line arrow-parens
export const withL10n = <Props: {}>(
  Component: React.ComponentType<Props>,
): (React.ComponentType<
  $Diff<
    Props,
    {
      translations: Translations | void,
      locale: string | void,
      translate?: TranslateType,
      forwardedRef?: (mixed) => ?HTMLElement,
    },
  >,
>) => {
  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  function WrapperComponent(passedThroughProps: Props) {
    return (
      <L10nContext.Consumer>
        {/* ⚠️ This typing is perhaps a bit confusing... */}
        {({
          locale,
          translations,
        }: {
          locale: string,
          translations: Translations,
        }) => (
          <Component
            {...passedThroughProps}
            locale={locale}
            translations={translations}
            translate={factory(locale, translations)}
          />
        )}
      </L10nContext.Consumer>
    );
  }

  return React.forwardRef((props, ref) => (
    <WrapperComponent {...props} forwardedRef={ref} />
  ));
};
